import classNames from "classnames";
import { Disclosure } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  NavItemType,
  NavbarRendererVariant,
  type ConfigNavbarProps
} from "./types";
import { NavbarProvider } from "./NavbarContext";
import { NavbarRenderer } from "./NavbarRenderer";
import { ProfileMenu } from "./ProfileMenu";
import {
  filterVisibleItems,
  isItemVisible,
  sortByPosition,
  themeToCssVariables
} from "./utils";

export function Navbar({
  config,
  user,
  executeAction,
  className
}: ConfigNavbarProps) {
  const { themeConfig, topNav, profileMenu } = config;
  const themeStyles = themeToCssVariables(themeConfig);
  const visibleTopNav = sortByPosition(filterVisibleItems(topNav));
  const logoItem = visibleTopNav.find((item) => item.type === NavItemType.Logo);
  const navItems = visibleTopNav.filter(
    (item) => item.type !== NavItemType.Logo
  );

  return (
    <NavbarProvider
      theme={themeConfig}
      user={user}
      executeAction={executeAction}
    >
      <Disclosure
        as="nav"
        className={classNames("sticky top-0 z-30 w-full", className)}
      >
        {({ open }) => (
          <>
            <div
              className="mx-auto max-w-screen-3xl px-4 sm:px-22 lg:px-14"
              style={{
                ...themeStyles,
                backgroundColor: "var(--navbar-bg)",
                color: "var(--navbar-text)",
                borderBottom: "1px solid var(--navbar-border)"
              }}
            >
              <div className="hidden sm:block">
                <div className="grid h-14 w-full grid-cols-[minmax(7.5rem,20%)_1fr_auto] items-center">
                  <div className="flex items-center">
                    {logoItem && (
                      <NavbarRenderer
                        item={logoItem}
                        variant={NavbarRendererVariant.Top}
                      />
                    )}
                  </div>

                  <div className="flex h-full items-center pl-4 sm:gap-3 md:gap-0 md:space-x-16">
                    {navItems.map((item) => (
                      <NavbarRenderer
                        key={item.id}
                        item={item}
                        variant={NavbarRendererVariant.Top}
                      />
                    ))}
                  </div>

                  <div className="flex items-center justify-end sm:ml-6">
                    {profileMenu && isItemVisible(profileMenu) && (
                      <ProfileMenu item={profileMenu} />
                    )}
                  </div>
                </div>
              </div>

              <div className="sm:hidden">
                <div className="flex h-14 w-full items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Disclosure.Button
                      className="inline-flex items-center justify-center rounded-md p-2 focus:outline-none"
                      style={{ color: "var(--navbar-text)" }}
                      aria-label={open ? "Close menu" : "Open menu"}
                    >
                      {open ? (
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      ) : (
                        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                      )}
                    </Disclosure.Button>
                    {logoItem && (
                      <NavbarRenderer
                        item={logoItem}
                        variant={NavbarRendererVariant.Top}
                      />
                    )}
                  </div>

                  {profileMenu && isItemVisible(profileMenu) && (
                    <ProfileMenu item={profileMenu} />
                  )}
                </div>
              </div>
            </div>

            <Disclosure.Panel
              className="sm:hidden"
              style={{
                ...themeStyles,
                backgroundColor: "var(--navbar-bg)",
                borderBottom: "1px solid var(--navbar-border)"
              }}
            >
              <div className="space-y-1 px-2 pb-3 pt-2">
                {visibleTopNav
                  .filter((item) => item.type !== NavItemType.Logo)
                  .map((item) => (
                    <NavbarRenderer
                      key={item.id}
                      item={item}
                      variant={NavbarRendererVariant.Mobile}
                    />
                  ))}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </NavbarProvider>
  );
}

export default Navbar;
